import {
  ReactElement, useCallback, useEffect, useRef, useState,
} from 'react';
import { MapContentContainer } from './styles';
import { MapChart } from './components/MapChart';
import { useFilterContext } from '../contexts/FilterContext';
import { useMap } from './hooks/useMap';
import { useDebounce } from '~/hooks';
import { MapInfo } from './components/MapInfo';
import { useLatestRef } from './hooks/useLatestRef';
import { MapUnitInfo } from './types';
import { buildUnitMap, sameStatesArray } from './helpers';
import { HealthUnitsInfo } from '../contexts/HealthDataContext';
import { ThermalComfortUnitsInfo } from '../contexts/ThermalComfortDataContext';
import { parseUnitThermalComfortStatus } from '../helpers/thermalComfort';
import { ThermalComfortStatusFilter } from '~/providers/types/overview';

type MapContentProps = | {
      type: 'health';
      disabled: boolean;
      units: HealthUnitsInfo;
    }
  | {
      type: 'thermalComfort';
      disabled: boolean;
      units: ThermalComfortUnitsInfo;
    };

export type UnitMap = Map<string, HealthUnitsInfo | ThermalComfortUnitsInfo>;

type UnitsState = {
  type: 'thermalComfort' | 'health';
  allMap: UnitMap;
  stateMap: UnitMap;
};

export function MapContent({
  disabled,
  units,
  type,
}: Readonly<MapContentProps>): ReactElement {
  const {
    client, selectedOptions, onSelectOption, filterOptions,
    clearFilter,
  } = useFilterContext();
  const [hoveredState, setHoveredState] = useState<string>();
  const [selectedState, setSelectedState] = useState<string>();
  const [hoveredUnit, setHoveredUnit] = useState<MapUnitInfo>();
  const [selectedUnit, setSelectedUnit] = useState<MapUnitInfo>();
  const [unitState, setUnitState] = useState<UnitsState>({
    allMap: new Map(),
    stateMap: new Map(),
    type,
  });
  const unitsStateMapReference = useRef<UnitMap>(new Map());
  const [scope, setScope] = useState<'state' | 'unit'>('state');

  const debouncedHoverState = useDebounce(hoveredState, 200);
  const debouncedHoverUnit = useDebounce(hoveredUnit, 200);
  const debouncedScope = useDebounce(scope, 200);
  const debouncedSelectedState = useDebounce(selectedState, 200);
  const debouncedSelectedUnit = useDebounce(selectedUnit, 100);

  const firstRender = useRef(true);
  const prevUnit = useRef<string>();
  const prevClient = useRef<string>();
  const prevGroup = useRef<string>();
  const prevHealth = useRef<string>();
  const prevStatus = useRef<string>();
  const prevState = useRef<string[]>();

  const scopeRef = useLatestRef(scope);

  const {
    updateStateMap, goCountryMap, updateCountryMap, onCloseUnitCard,
  } = useMap({
    stateEvents: {
      onClick: (state) => onClickState(state, unitsStateMapReference.current),
      onPointerOut: onPointerOutState,
      onPointerOver: onPointerOverState,
    },
    unitEvents: {
      onClick: onClickUnit,
      onPointerOut: onPointerOutUnit,
      onPointerOver: onPointerOverUnit,
    },
    disabled,
    onClickResetButton,
  });

  const updateUnitsMap = useCallback((map: UnitMap): void => {
    setUnitState((previousValues) => ({
      ...previousValues,
      stateMap: map,
    }));
    if (selectedState) {
      updateStateMap(selectedState, map);
    } else {
      updateCountryMap(map);
    }
  }, [selectedState, updateCountryMap, updateStateMap]);

  function handleScope(newScope: 'state' | 'unit'): void {
    if (newScope === scopeRef.current) return;

    setScope(newScope);
  }

  function onClickState(newState: string, map: UnitMap): void {
    handleScope('state');
    const foundState = filterOptions.states.find((item) => item.label === newState);
    if (!foundState) return;

    setSelectedState(newState);
    setHoveredState(undefined);
    clearFilter('state');
    onSelectOption('state', foundState.value);

    updateStateMap(newState, map);
  }

  function onPointerOutState(): void {
    handleScope('state');
    setHoveredState(undefined);
  }

  function onPointerOverState(newState: string): void {
    handleScope('state');
    setHoveredState(newState);
  }

  function onClickUnit(unitId: string, info: MapUnitInfo): void {
    handleScope('unit');
    setSelectedState(info.state);
    setSelectedUnit(info);
    prevUnit.current = unitId;
  }

  function onPointerOverUnit(unitId: string, info: MapUnitInfo): void {
    handleScope('unit');
    setSelectedState(info.state);
    setHoveredUnit(info);
  }

  function onPointerOutUnit(): void {
    if (!prevUnit.current) {
      setScope('state');
      setHoveredUnit(undefined);
    }
  }

  function clearStateFields(): void {
    const foundState = filterOptions.states.find((item) => item.label === selectedState)?.value as string;
    onSelectOption('state', foundState);

    setSelectedState(undefined);
    setHoveredState(undefined);
  }

  function clearUnitFields(): void {
    setSelectedUnit(undefined);
    setHoveredUnit(undefined);
    prevUnit.current = undefined;
  }

  function onCloseInfoCard(): void {
    if (scope === 'state') {
      if (selectedState && hasOnlyCurrentStateData()) {
        clearFilter('healthStatus');
      }
      clearStateFields();
      goCountryMap();
    } else {
      onCloseUnitCard();
      handleScope('state');
      clearUnitFields();
    }
  }

  function hasOnlyCurrentStateData(): boolean {
    return unitState.stateMap.size === 1;
  }

  function onClickResetButton(): void {
    if (prevUnit.current) {
      onCloseUnitCard();
      clearUnitFields();
    }
    clearStateFields();
    clearFilter('state');
    handleScope('state');
  }

  function filterUnitByOptions(unitInfo: HealthUnitsInfo[number] | ThermalComfortUnitsInfo[number]): boolean {
    const conditions: boolean[] = [];

    if (selectedOptions.group) {
      conditions.push(selectedOptions.group.units.includes(Number(unitInfo.id)));
    }

    if (type === 'health' && selectedOptions.healthStatus) {
      conditions.push(selectedOptions.healthStatus === (unitInfo as HealthUnitsInfo[number]).health);
    } else if (selectedOptions.status) {
      conditions.push(
        parseUnitThermalComfortStatus(selectedOptions.status as ThermalComfortStatusFilter) === (unitInfo as ThermalComfortUnitsInfo[number]).thermalComfort,
      );
    }

    if (selectedOptions.state && selectedOptions.state.length > 0) {
      const selectedStateLabels = new Set(
        selectedOptions.state.map((stateId) => filterOptions.states.find((item) => item.value === stateId)?.label).filter(Boolean),
      );
      conditions.push(selectedStateLabels.has(unitInfo.state));
    }

    return conditions.every(Boolean);
  }

  function filterUnitMap(allMap?: UnitMap): UnitMap {
    const newMap = new Map<string, HealthUnitsInfo | ThermalComfortUnitsInfo>();
    const map = allMap ?? unitState.allMap;

    for (const [key, value] of map) {
      const filteredValues = value.filter(filterUnitByOptions);
      newMap.set(key, filteredValues as HealthUnitsInfo | ThermalComfortUnitsInfo);
    }

    return newMap;
  }

  function setFilterStateOnMap(): void {
    if (selectedOptions.state?.length === 1 && !selectedState) {
      const [stateId] = selectedOptions.state;
      const foundState = filterOptions.states.find((item) => item.value === stateId)?.label as string;
      setSelectedState(foundState);
    }

    if (selectedOptions.state && selectedOptions.state.length > 1 || (selectedState && !selectedOptions.state?.length)) {
      if (selectedState) {
        setSelectedState(undefined);
      }
      goCountryMap();
    }
  }

  function getMapInfoName(): string | undefined {
    if (debouncedScope === 'state') {
      return debouncedSelectedState ?? debouncedHoverState;
    }

    return debouncedSelectedUnit?.name ?? debouncedHoverUnit?.name;
  }

  function getUnitInfo(): { id: number, city: string } | undefined {
    if (debouncedScope === 'state') return undefined;

    if (debouncedSelectedUnit) {
      return {
        city: debouncedSelectedUnit.city,
        id: Number(debouncedSelectedUnit.id),
      };
    }

    if (debouncedHoverUnit) {
      return {
        city: debouncedHoverUnit.city,
        id: Number(debouncedHoverUnit.id),
      };
    }

    return undefined;
  }

  function handleFilterChanges(): void {
    const clientHasChanged = prevClient.current !== client;
    const groupHasChanged = prevGroup.current !== selectedOptions.group?.value;
    const healthHasChanged = prevHealth.current !== selectedOptions.healthStatus;
    const thermalComfortStatusHasChanged = prevStatus.current !== selectedOptions.status;
    const stateHasChanged = !sameStatesArray(prevState.current ?? [], selectedOptions.state ?? []);

    const hasChanged = clientHasChanged || groupHasChanged || healthHasChanged || stateHasChanged || thermalComfortStatusHasChanged;

    if (clientHasChanged) {
      prevClient.current = client;
    }

    if (groupHasChanged) {
      prevGroup.current = selectedOptions.group?.value;
    }

    if (healthHasChanged) {
      prevHealth.current = selectedOptions.healthStatus;
    }

    if (stateHasChanged) {
      prevState.current = selectedOptions.state;
    }

    if (thermalComfortStatusHasChanged) {
      prevStatus.current = selectedOptions.status;
    }

    const groupMap = buildUnitMap(units);

    let needGroupMap = false;

    const isFirstRender = firstRender.current && !disabled;

    if (units.length > 0 && (isFirstRender || clientHasChanged)) {
      needGroupMap = true;
      setUnitState((previousValues) => ({
        ...previousValues,
        allMap: groupMap,
      }));
    }

    const filteredMap = filterUnitMap(needGroupMap ? groupMap : undefined);

    if (isFirstRender || hasChanged) {
      if (isFirstRender) {
        firstRender.current = false;
      }

      setUnitState((previousValues) => ({
        ...previousValues,
        stateMap: filteredMap,
      }));
      updateUnitsMap(filteredMap);
    }
  }

  function renderMapInfo(state: UnitsState) {
    if (state.type === 'health') {
      return (
        <MapInfo
          type="health"
          name={getMapInfoName()}
          unitInfo={getUnitInfo()}
          onCloseInfoCard={onCloseInfoCard}
          scope={debouncedScope}
          selected={debouncedScope === 'state' ? !!debouncedSelectedState : !!debouncedSelectedUnit}
          state={debouncedScope === 'unit' ? debouncedSelectedState : undefined}
          unitsMap={state.stateMap as Map<string, HealthUnitsInfo>}
        />
      );
    }

    return (
      <MapInfo
        type="thermalComfort"
        name={getMapInfoName()}
        unitInfo={getUnitInfo()}
        onCloseInfoCard={onCloseInfoCard}
        scope={debouncedScope}
        selected={debouncedScope === 'state' ? !!debouncedSelectedState : !!debouncedSelectedUnit}
        state={debouncedScope === 'unit' ? debouncedSelectedState : undefined}
        unitsMap={state.stateMap as Map<string, ThermalComfortUnitsInfo>}
      />
    );
  }

  useEffect(() => {
    setFilterStateOnMap();
  }, [selectedOptions.state]);

  useEffect(() => {
    if (!units) return;
    handleFilterChanges();
  }, [units]);

  useEffect(() => {
    unitsStateMapReference.current = unitState.stateMap;
  }, [unitState.stateMap]);

  useEffect(() => () => {
    unitsStateMapReference.current = new Map();
    prevClient.current = undefined;
    prevGroup.current = undefined;
    prevHealth.current = undefined;
    prevUnit.current = undefined;
    firstRender.current = true;
  }, []);

  return (
    <MapContentContainer>
      <MapChart />
      {renderMapInfo(unitState)}
    </MapContentContainer>
  );
}
